/**
 * The privileged Peer Interface editor (ADR-036 §D-5 / §D-7; Flow W).
 *
 * One scrollable form covering the whole lifecycle the storyboard asks for —
 * *choose type → configure connections → define filter → install (cert material)
 * → start/stop* — for `admin` / `support_engineer` only. Non-privileged sessions
 * never render this component; they get :func:`PeerInterfaceSummary` instead,
 * because the BFF withholds the endpoints, cert references and filter literals
 * this form is made of.
 *
 * # The two axes of "is it running"
 *
 * ``enabled`` decides whether the interface loads at all; ``state`` decides
 * whether it emits. They are separate on purpose and are presented separately:
 * disabling is a decommission, stopping is a kill switch, and an operator
 * reaching for one should not silently get the other.
 *
 * # Save is not start
 *
 * The form's Save issues the whole-document PUT. Start/stop is a **different
 * button on a different endpoint**, so it lands as its own audited event and
 * takes effect without carrying whatever else is half-typed in the form. A new
 * interface is created stopped and there is no "save and start" — see
 * :func:`newInterfaceConfig`.
 *
 * # Client-side validation mirrors, never replaces, the server's
 *
 * The checks here exist so an operator sees a duplicate connection name before
 * a 409, not so the client decides what is legal. The BFF re-validates
 * everything (ADR-009 defence-in-depth), and where the two ever disagree the
 * server wins and its message is what the form shows.
 *
 * Cross-references:
 *   - ADR-036 §D-1 (types), §D-4 (filter), §D-5 (config), §D-7 (RBAC + state).
 *   - REQ-PEER-029/-031/-033/-040/-041/-043/-044/-050.
 *   - Flow W (docs/engineering/detail/storyboards/flows/W-configure-peer-integration).
 */
import { useEffect, useMemo, useState } from "react";
import {
  CREATABLE_INTERFACE_TYPES,
  DELIVERY_PROFILES,
  TRANSPORT_SECURITY_MODES,
  allowedTransportSecurity,
  findInterfaceType,
  findTransportSecurity,
  supportsDeliveryProfile,
  transportSecurityFixedReason,
  type TransportSecurity,
} from "@/peers/peer-catalog";
import { PeerCertUpload } from "@/peers/peer-cert-upload";
import { useListenPortRanges } from "@/chrome/use-deployment-config";
import { portOutsidePublishedRange } from "@/lib/listen-port-range";
import { PeerInterfaceConnectionEditor } from "@/peers/peer-connection-editor";
import { PeerFilterBuilder } from "@/peers/peer-filter-builder";
import { toFilterConfig } from "@/peers/peer-filter-tree";
import { PeerSelect, PeerTextInput } from "@/peers/peer-form-primitives";
import { StatePill, StateToggleButton } from "@/peers/peer-state-controls";
import {
  newConnectionConfig,
  type PeerInterfaceConnectionConfig,
  type PeerInterfaceConfig,
} from "@/peers/peer-model";
import type { InterfaceState } from "@/peers/peer-catalog";
import { PeerSection } from "@/peers/peer-section";
import { resolveEffectiveState, type ChainLink } from "@/peers/peer-effective-state";
import { PeerSuppressionNote } from "@/peers/peer-suppression-note";

/**
 * What the interface-level start/stop does, disclosed from the State header.
 *
 * Architect-specified wording (2026-10-10), and a standing paragraph until
 * then. Shorter than the provider twin
 * (:data:`PROVIDER_INTERFACE_STATE_HELP`) because there is one less fact to
 * state: an egress interface has a spool behind it where a provider interface
 * has nothing, so "data are not queued" is true on ingress and would be false
 * here.
 */
export const PEER_INTERFACE_STATE_HELP = "Start/Stop takes effect immediately.";

/** Props for {@link PeerInterfaceEditor}. */
export interface PeerInterfaceEditorProps {
  /** The interface being edited, as a complete PUT body. */
  value: PeerInterfaceConfig;
  /** True when this interface has never been persisted (drives copy + state control). */
  isNew: boolean;
  onSave: (config: PeerInterfaceConfig) => void;
  onDelete: () => void;
  onCancel: () => void;
  onSetState: (state: InterfaceState) => void;
  onSetConnectionState: (connectionId: string, state: InterfaceState) => void;
  /**
   * Delete one PERSISTED connection on the server (REQ-PEER-105, REQ-UI-154).
   *
   * Optional: a caller that does not supply it gets draft-only removal, which is
   * what the standalone (non-dialog) mount has — there is no peer id there to
   * address the nested route with.
   */
  onDeleteConnection?: (connectionId: string) => void;
  savePending?: boolean;
  statePending?: boolean;
  /** Server-side error text to surface verbatim above the buttons. */
  saveError?: string | null;
  /** Names of the other interfaces, for the local uniqueness hint (REQ-PEER-050). */
  otherNames: readonly string[];
  /** Mints ids for new connections; injectable so tests are deterministic. */
  makeId?: () => string;
  /**
   * The owning chain, root-first — ``[peer]`` when mounted in the dialog.
   *
   * Threaded down to the connection level so a connection under a stopped peer
   * says so rather than reading as running (REQ-UI-144). Defaults to empty for
   * the standalone case, where there is no ancestor to suppress anything.
   */
  ancestors?: readonly ChainLink[];
}

/** Render the full interface editor. */
export function PeerInterfaceEditor({
  value,
  isNew,
  onSave,
  onDelete,
  onCancel,
  onSetState,
  onSetConnectionState,
  onDeleteConnection,
  savePending = false,
  statePending = false,
  saveError,
  otherNames,
  makeId = () => crypto.randomUUID(),
  ancestors = [],
}: PeerInterfaceEditorProps): React.ReactElement {
  const [draft, setDraft] = useState<PeerInterfaceConfig>(value);
  const [confirmDelete, setConfirmDelete] = useState(false);

  // Re-seed when the selection changes, or when a save round-trips a new
  // server-side value. Keyed on identity + the fields the server can change
  // underneath the form (state flips arrive from the start/stop endpoint).
  useEffect(() => {
    setDraft(value);
    setConfirmDelete(false);
  }, [value]);

  /**
   * Which connections exist on the server.
   *
   * Derived from `value` — the document the server sent — and never from
   * `draft`, which also holds rows the operator just added. A draft-only row has
   * nothing to DELETE, and addressing the nested route with its id would 404 on
   * a control that looks identical to the one beside it.
   */
  const persistedConnectionIds = useMemo(
    () => new Set(value.connections.map((c) => c.connection_id)),
    [value.connections],
  );

  const descriptor = findInterfaceType(draft.type_id);
  const payloadDomain = descriptor?.payloadDomain ?? "sapient";
  const patch = (next: Partial<PeerInterfaceConfig>): void =>
    setDraft((current) => ({ ...current, ...next }));

  const nameError = useMemo(() => {
    const trimmed = draft.name.trim();
    if (trimmed === "") return "A name is required.";
    if (otherNames.includes(trimmed)) return "Another interface already uses this name.";
    return null;
  }, [draft.name, otherNames]);

  const duplicateConnectionNames = useMemo(() => {
    const counts = new Map<string, number>();
    for (const connection of draft.connections) {
      const key = connection.name.trim();
      counts.set(key, (counts.get(key) ?? 0) + 1);
    }
    return new Set([...counts.entries()].filter(([, n]) => n > 1).map(([name]) => name));
  }, [draft.connections]);

  // REQ-UI-198: a `pull` port outside the published range is refused here
  // with the BFF's own wording, so Save is blocked for the same reason the
  // server would give. An unadvertised range blocks nothing — the server stays
  // the authority.
  const publishedRange = useListenPortRanges()?.peer ?? null;
  const unpublishedPullPorts = useMemo(
    () =>
      draft.connections.filter(
        (connection) =>
          connection.role === "pull" &&
          portOutsidePublishedRange(connection.endpoint_port, publishedRange),
      ).length,
    [draft.connections, publishedRange],
  );

  const blocked =
    nameError !== null || duplicateConnectionNames.size > 0 || unpublishedPullPorts > 0;

  // REQ-UI-152. The permitted set is the TYPE's, and a mode outside it is absent
  // from the control rather than disabled — see `allowedTransportSecurity`.
  const allowedModes = allowedTransportSecurity(draft.type_id);
  const currentMode = findTransportSecurity(draft.transport_security);
  const fixedModeReason = transportSecurityFixedReason(draft.type_id);
  const isPlaintext = draft.transport_security === "plaintext";
  /**
   * The stored mode is outside what this type permits — a cross-level
   * disagreement, and the row is shown as it is rather than coerced.
   *
   * Reachable three ways: a document written before a type's permitted set
   * narrowed, a row persisted by a newer release, and — the one this codebase
   * has actually met — a type change that left the mode behind. **Never
   * silently corrected**, because the row is what the server holds and what
   * assembly will refuse; a UI that quietly rendered the type's constraint
   * instead would show a valid-looking interface the server 422s, with the
   * disagreement invisible on both sides. So both facts are stated and the
   * operator picks. The single-permitted-mode branch below is suppressed in this
   * state for the same reason: it would assert the type's constraint as though
   * the row already satisfied it.
   */
  const modeDisagreesWithType = !allowedModes.includes(draft.transport_security);

  /**
   * Which trust fields apply, from the SELECTED MODE rather than from the type
   * (REQ-UI-152; Flow W Frame 7; feature-spec §5.4).
   *
   * `plaintext` presents and verifies nothing; `tls` verifies the peer and
   * presents nothing, so it needs no leaf and no key; `mtls` uses all three.
   * Keying this on the type — as it was before — left a live plaintext interface
   * showing three cert fields that configure nothing, which is inert
   * configuration reading as a control that exists.
   */
  const showCaBundle = draft.transport_security !== "plaintext";
  const showMeridianLeaf = draft.transport_security === "mtls";

  const setType = (type_id: string): void => {
    // Clearing the now-inapplicable fields rather than hiding them: the config
    // validator rejects a delivery profile on a CDS interface, so a retained
    // value would be an invisible 422 waiting on the next save.
    //
    // Transport security is clamped for the same reason and it is the sharper
    // case: a `sapient-data` interface set to `mtls` and retyped to `sapient-cds`
    // would carry a mode that type's descriptor refuses, so the save 422s on a
    // field the operator can no longer see. Falling back to the new type's own
    // default keeps the document valid — and that default is fail-safe by
    // construction (`mtls` wherever the type permits it).
    const nextAllowed = allowedTransportSecurity(type_id);
    const nextMode: TransportSecurity = nextAllowed.includes(draft.transport_security)
      ? draft.transport_security
      : (findInterfaceType(type_id)?.defaultTransportSecurity ?? "mtls");
    patch({
      type_id,
      transport_security: nextMode,
      delivery_profile: supportsDeliveryProfile(type_id) ? draft.delivery_profile : null,
      // Cert references follow the resolved MODE, not the type — the same rule the
      // fields render by, applied to what is persisted, so a mode change cannot
      // leave a reference behind that nothing displays.
      peer_ca_bundle_ref: nextMode !== "plaintext" ? draft.peer_ca_bundle_ref : null,
      meridian_cert_ref: nextMode === "mtls" ? draft.meridian_cert_ref : null,
      meridian_key_ref: nextMode === "mtls" ? draft.meridian_key_ref : null,
    });
  };

  /** Change the wire-security mode, clearing whatever it makes inapplicable. */
  const setTransportSecurity = (mode: string): void => {
    const next = mode as TransportSecurity;
    patch({
      transport_security: next,
      peer_ca_bundle_ref: next !== "plaintext" ? draft.peer_ca_bundle_ref : null,
      meridian_cert_ref: next === "mtls" ? draft.meridian_cert_ref : null,
      meridian_key_ref: next === "mtls" ? draft.meridian_key_ref : null,
    });
  };

  const patchConnection = (index: number, next: PeerInterfaceConnectionConfig): void =>
    patch({ connections: draft.connections.map((c, i) => (i === index ? next : c)) });

  return (
    <form
      data-testid="peer-interface-editor"
      // No scroll container of its own: the dialog is ONE scrollable pane with
      // sticky section headers (REQ-UI-141, ADR-024 §4). A nested scroller here
      // would strand a section header inside a box the operator has to find
      // before they can scroll it.
      onSubmit={(event) => {
        event.preventDefault();
        if (!blocked) onSave({ ...draft, name: draft.name.trim() });
      }}
    >
      <div>
        <PeerSection id="identity" title="Interface">
          <div className="flex flex-col gap-3">
            <PeerTextInput
              label="Name"
              value={draft.name}
              maxLength={128}
              onChange={(name) => patch({ name })}
              error={nameError}
              testId="peer-interface-name"
              placeholder="north-ridge-fusion"
              hint="Unique across all interfaces. Appears on the System display."
            />
            <PeerSelect
              label="Type"
              value={draft.type_id}
              testId="peer-interface-type"
              // Creatable types only: offering one that cannot then be started
              // would be an affordance leading nowhere (REQ-UI-146). An interface
              // already configured with a reserved type never reaches this editor —
              // it renders read-only.
              options={CREATABLE_INTERFACE_TYPES.map((t) => ({
                value: t.typeId,
                label: t.label,
              }))}
              onChange={setType}
              hint={descriptor?.description ?? `Unrecognized type "${draft.type_id}".`}
            />
            {supportsDeliveryProfile(draft.type_id) && (
              <PeerSelect
                label="Delivery profile"
                value={draft.delivery_profile ?? "framed_ack"}
                testId="peer-interface-delivery-profile"
                options={DELIVERY_PROFILES.map((p) => ({ value: p.value, label: p.label }))}
                onChange={(delivery_profile) =>
                  patch({
                    delivery_profile: delivery_profile as PeerInterfaceConfig["delivery_profile"],
                  })
                }
                hint={
                  DELIVERY_PROFILES.find(
                    (p) => p.value === (draft.delivery_profile ?? "framed_ack"),
                  )?.hint
                }
              />
            )}
            {!supportsDeliveryProfile(draft.type_id) && descriptor !== null && (
              <p className="text-meta text-text-muted">
                CDS delivery semantics are fixed by the accredited transfer contract and are not
                selectable.
              </p>
            )}
            {/* REQ-UI-152. One permitted mode is not a choice: a single-option
                select is indistinguishable from a broken control, so it renders
                as a stated fact with the constraint's reason beside it. */}
            {allowedModes.length === 1 && !modeDisagreesWithType ? (
              <div className="flex flex-col gap-0.5" data-testid="peer-transport-security-fixed">
                <span className="text-body-sm text-text-secondary">Transport security</span>
                <span className="text-body-sm text-text-primary">
                  {currentMode?.label ?? draft.transport_security}
                </span>
                <span className="text-meta text-text-muted">
                  {fixedModeReason ?? "Fixed by this interface type."}
                </span>
              </div>
            ) : (
              <PeerSelect
                label="Transport security"
                value={draft.transport_security}
                testId="peer-transport-security"
                // The stored mode is included even when the type forbids it, so
                // the control shows what the row actually says. Dropping it would
                // make the select render a DIFFERENT value than the document
                // holds — the silent-rewrite failure `toInterfaceConfig` already
                // guards against on save, reproduced in the form.
                options={TRANSPORT_SECURITY_MODES.filter(
                  (m) => allowedModes.includes(m.value) || m.value === draft.transport_security,
                ).map((m) => ({ value: m.value, label: m.label }))}
                onChange={setTransportSecurity}
                hint={currentMode?.hint}
              />
            )}
            {modeDisagreesWithType && (
              <p
                role="alert"
                data-testid="peer-transport-security-disagreement"
                className="rounded border border-warning bg-warning/10 px-2 py-1 text-meta text-warning"
              >
                This interface is stored as{" "}
                <strong>{currentMode?.label ?? draft.transport_security}</strong>, which its type
                does not permit — {fixedModeReason ?? "the type constrains this field."} It is shown
                as stored rather than corrected; saving it unchanged will be refused. Choose a
                permitted mode, or change the type.
              </p>
            )}
            {/* The consequence at SELECTION. Its twin at confirmation lives above
                the footer's Save — REQ-UI-152 requires both, in the same words,
                because they answer different questions and an operator who set
                this field rows ago is no longer looking at this one. */}
            {/* ⚠ No `bg-destructive/10` here, and that is measured rather than a
                taste call. `text-destructive` over its own 10% tint composites to
                #c83932 on #f1e4e5 = **4.15:1** in the light theme, under
                REQ-UI-046's 4.5:1 text bar; on the plain panel the same ink is
                ~5.06:1. The border carries the alarm as a non-text channel and
                the words carry the meaning, so nothing is lost. Found by the
                §5.11 live pass — every unit test was green, because jsdom
                composites nothing. */}
            {isPlaintext && (
              <p
                role="status"
                data-testid="peer-plaintext-consequence"
                className="rounded border border-destructive px-2 py-1 text-meta text-destructive"
              >
                {findTransportSecurity("plaintext")?.consequence}
              </p>
            )}

            {/* REQ-UI-153. The PROFILE is privileged and this editor only ever
                renders for a privileged session, so it is unconditional here.
                The MODE is offered only once a profile is set: migration 057's
                CHECK refuses a non-`legacy` mode without one, so the control
                would otherwise lead straight to a 422. */}
            <PeerTextInput
              label="Conversion profile"
              value={draft.conversion_profile_ref ?? ""}
              mono
              onChange={(ref) => {
                const conversion_profile_ref = ref.trim() === "" ? null : ref;
                patch({
                  conversion_profile_ref,
                  // Clearing the profile must take the mode with it, or the save
                  // carries `engine` with no ref and is refused by the CHECK.
                  conversion_mode:
                    conversion_profile_ref === null ? "legacy" : draft.conversion_mode,
                });
              }}
              testId="peer-conversion-profile"
              placeholder="sha256:… — no profile installed on this interface"
              hint="The installed (peer schema, conversion spec) pair, by digest."
            />
            {draft.conversion_profile_ref === null ? (
              <div className="flex flex-col gap-0.5" data-testid="peer-conversion-mode-fixed">
                <span className="text-body-sm text-text-secondary">Conversion mode</span>
                <span className="text-body-sm text-text-primary">
                  Legacy — the built-in renderer is authoritative
                </span>
                <span className="text-meta text-text-muted">
                  Install a profile before selecting another mode; a mode without a profile is
                  refused.
                </span>
              </div>
            ) : (
              <PeerSelect
                label="Conversion mode"
                value={draft.conversion_mode}
                testId="peer-conversion-mode"
                options={[
                  { value: "legacy", label: "Legacy — the built-in renderer is authoritative" },
                  {
                    value: "engine",
                    label: "Engine — the installed conversion profile is authoritative",
                  },
                ]}
                onChange={(mode) =>
                  patch({ conversion_mode: mode as PeerInterfaceConfig["conversion_mode"] })
                }
                hint={
                  draft.conversion_mode === "engine"
                    ? "What leaves on this interface is rendered from the installed profile, not from built-in code."
                    : "What leaves on this interface is rendered by Meridian's built-in code for its type."
                }
              />
            )}

            <PeerTextInput
              label="Description"
              value={draft.metadata?.description ?? ""}
              onChange={(description) => patch({ metadata: { ...draft.metadata, description } })}
              testId="peer-interface-description"
              hint="Free-form operator annotation — who consumes this, and why."
            />
            <label className="flex items-center gap-2 text-body-sm text-text-primary">
              <input
                type="checkbox"
                checked={draft.enabled}
                data-testid="peer-interface-enabled"
                onChange={(event) => patch({ enabled: event.target.checked })}
                className="h-3.5 w-3.5 cursor-pointer"
              />
              Enabled — the service loads this interface at all. Distinct from started/stopped.
            </label>
          </div>
        </PeerSection>

        <PeerSection
          id="connections"
          title="Consumer connections"
          action={
            <button
              type="button"
              data-testid="peer-add-connection"
              onClick={() =>
                patch({ connections: [...draft.connections, newConnectionConfig(makeId())] })
              }
              className="rounded border border-border px-2 py-0.5 text-meta text-text-primary hover:bg-bg-elevated"
            >
              Add connection
            </button>
          }
        >
          {draft.connections.length === 0 ? (
            <p className="text-body-sm text-text-muted" data-testid="peer-no-connections">
              No connections. This interface has nowhere to send to and will emit nothing.
            </p>
          ) : (
            <div className="flex flex-col gap-2">
              {draft.connections.map((connection, index) => (
                <PeerInterfaceConnectionEditor
                  key={connection.connection_id}
                  connection={connection}
                  index={index}
                  unsaved={isNew}
                  nameError={
                    duplicateConnectionNames.has(connection.name.trim())
                      ? "Duplicate connection name within this interface."
                      : null
                  }
                  onChange={(next) => patchConnection(index, next)}
                  onRemove={() =>
                    patch({ connections: draft.connections.filter((_, i) => i !== index) })
                  }
                  onDelete={
                    onDeleteConnection !== undefined &&
                    persistedConnectionIds.has(connection.connection_id)
                      ? () => onDeleteConnection(connection.connection_id)
                      : undefined
                  }
                  onToggleState={(next) => onSetConnectionState(connection.connection_id, next)}
                  publishedRange={publishedRange}
                  suppression={resolveEffectiveState(
                    [...ancestors, { name: draft.name, state: draft.state }],
                    connection.state,
                  )}
                />
              ))}
            </div>
          )}
        </PeerSection>

        {/* Disclosed by the selected MODE, not by the type (REQ-UI-152; §5.4).
            The section always renders — a `plaintext` interface states that it
            presents and verifies nothing, rather than the section vanishing and
            leaving an operator to wonder where the trust material went. */}
        <PeerSection id="trust" title="Trust material">
          <div className="flex flex-col gap-3">
            {!showCaBundle && (
              <p className="text-body-sm text-text-muted" data-testid="peer-trust-none">
                This interface runs in plaintext, so it presents and verifies no certificates. Trust
                material applies only under TLS or mTLS.
              </p>
            )}
            {showCaBundle && (
              <>
                <PeerTextInput
                  label="Consumer CA bundle path"
                  value={draft.peer_ca_bundle_ref ?? ""}
                  mono
                  onChange={(peer_ca_bundle_ref) => patch({ peer_ca_bundle_ref })}
                  testId="peer-ca-bundle-ref"
                  placeholder="/etc/meridian/peer-certs/consumer-ca.pem"
                  hint="The trust anchor deciding which Consumer Meridian will talk to."
                />
                <PeerCertUpload
                  kind="consumer_ca_bundle"
                  onStored={(peer_ca_bundle_ref) => patch({ peer_ca_bundle_ref })}
                />
              </>
            )}
            {showCaBundle && !showMeridianLeaf && (
              <p className="text-body-sm text-text-muted" data-testid="peer-trust-tls-only">
                Under TLS, Meridian verifies the Consumer&rsquo;s chain and presents no certificate
                of its own — so it needs no leaf and no key here. Select mTLS to present one.
              </p>
            )}
            {showMeridianLeaf && (
              <>
                <PeerTextInput
                  label="Meridian certificate path"
                  value={draft.meridian_cert_ref ?? ""}
                  mono
                  onChange={(meridian_cert_ref) => patch({ meridian_cert_ref })}
                  testId="peer-cert-ref"
                  placeholder="/etc/meridian/certs/peer-manager.crt"
                  hint="The leaf Meridian presents to this Consumer."
                />
                <PeerCertUpload
                  kind="meridian_cert"
                  onStored={(meridian_cert_ref) => patch({ meridian_cert_ref })}
                />
                <PeerTextInput
                  label="Meridian key path"
                  value={draft.meridian_key_ref ?? ""}
                  mono
                  onChange={(meridian_key_ref) => patch({ meridian_key_ref })}
                  testId="peer-key-ref"
                  placeholder="/etc/meridian/certs/peer-manager.key"
                  hint="A path reference only. Private keys are minted at install and never uploaded."
                />
              </>
            )}
          </div>
        </PeerSection>

        <PeerSection id="filter" title="Filter">
          <PeerFilterBuilder
            root={draft.filter?.root ?? null}
            payloadDomain={payloadDomain}
            onChange={(root) => patch({ filter: toFilterConfig(root) })}
          />
        </PeerSection>

        <PeerSection id="state" title="State" help={PEER_INTERFACE_STATE_HELP}>
          <div className="flex flex-col gap-2">
            <div className="flex flex-wrap items-center gap-2">
              <StatePill state={draft.state} />
              <PeerSuppressionNote
                resolved={resolveEffectiveState(ancestors, draft.state)}
                testId="interface-detail"
              />
              {isNew ? (
                <span className="text-meta text-text-muted" data-testid="peer-state-unsaved">
                  New interfaces are created stopped. Save first, then start it deliberately.
                </span>
              ) : (
                <StateToggleButton
                  state={draft.state}
                  pending={statePending}
                  onToggle={onSetState}
                  testId="peer-interface-state-toggle"
                  label="interface"
                />
              )}
            </div>
          </div>
        </PeerSection>
      </div>

      {saveError && (
        <p
          role="alert"
          data-testid="peer-save-error"
          className="px-4 py-2 text-body-sm text-destructive"
        >
          {saveError}
        </p>
      )}

      {/* REQ-UI-152's second statement, at CONFIRMATION. Deliberately the same
          sentence as the one beside the field — one constant in the catalog, so
          the two cannot drift into describing the same wire differently — with
          the act named, because this one answers "what am I about to make
          true?" rather than "what am I choosing?". */}
      {isPlaintext && (
        <p
          role="status"
          data-testid="peer-save-plaintext-consequence"
          className="border-t border-destructive px-4 py-2 text-meta text-destructive"
        >
          Saving leaves this interface in <strong>plaintext</strong>:{" "}
          {findTransportSecurity("plaintext")?.consequence}
        </p>
      )}

      <footer className="flex items-center gap-2 border-t border-border px-4 py-2">
        {!isNew &&
          (confirmDelete ? (
            <div className="flex items-center gap-2">
              <span className="text-meta text-destructive">Delete this interface permanently?</span>
              <button
                type="button"
                onClick={onDelete}
                data-testid="peer-delete-confirm"
                className="rounded bg-destructive px-2 py-0.5 text-meta text-destructive-foreground hover:opacity-90"
              >
                Delete
              </button>
              <button
                type="button"
                onClick={() => setConfirmDelete(false)}
                className="rounded border border-border px-2 py-0.5 text-meta text-text-primary hover:bg-bg-elevated"
              >
                Keep
              </button>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => setConfirmDelete(true)}
              data-testid="peer-delete"
              className="rounded border border-destructive px-2 py-0.5 text-meta text-destructive hover:bg-destructive/10"
            >
              Delete interface
            </button>
          ))}
        <div className="ml-auto flex items-center gap-2">
          <button
            type="button"
            onClick={onCancel}
            data-testid="peer-cancel"
            className="rounded border border-border px-3 py-1 text-body-sm text-text-primary hover:bg-bg-elevated"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={blocked || savePending}
            data-testid="peer-save"
            className="rounded bg-brand px-3 py-1 text-body-sm font-medium text-bg-base hover:bg-brand-dim disabled:opacity-50"
          >
            {savePending
              ? "Saving…"
              : isPlaintext
                ? // The button names the consequence too. An operator scanning
                  // for the primary action reads this and nothing else.
                  isNew
                  ? "Create in plaintext"
                  : "Save in plaintext"
                : isNew
                  ? "Create interface"
                  : "Save changes"}
          </button>
        </div>
      </footer>
    </form>
  );
}
